import { tv } from 'tailwind-variants'

import { typography } from '@gto/ui/typography'

export const chipVariants = tv({
  slots: {
    root: [
      // Layout — delete button is an in-flow child that collapses to w-0 and expands on hover.
      // Negative margin on hover compensates so the chip overlaps neighbors without shifting layout.
      // `z-0` pins each chip into its own stacking context so `hover:z-[10]` always wins over
      // siblings whose opacity/margin transitions are still in flight (otherwise a previously-
      // hovered chip can briefly paint over the newly-hovered one).
      'group/chip relative z-[0] inline-flex items-center justify-center rounded-[100px]',
      // Spacing — gap between icon / label / subchips / delete (0.5 = 4px, but Figma uses 2px)
      'gap-[2px]',
      // Transitions:
      // - `margin` / `z-index` keep the delete-reveal overlap retract smooth (no neighbor jump).
      // - `background-color` / `border-color` / `color` fade so chip-to-chip navigation doesn't
      //   flicker — the previously-hovered chip's hover bg eases back to its default while the
      //   newly-hovered chip's bg eases in, instead of both snapping.
      // - `opacity` fades the `ChipGroup` `:has(:hover)` sibling dim. Listed here (rather than
      //   installed by `ChipGroup` via a descendant selector) so it doesn't override this
      //   transition list; disabled chips mount with `opacity-[0.38]` and never change at
      //   runtime, so they're unaffected.
      'transition-[margin,z-index,background-color,border-color,color,opacity] duration-150',
      'hover:transition-[margin,background-color,border-color,color,opacity] hover:duration-150',
      // Misc
      'select-none whitespace-nowrap',
      // Reset button defaults — bg-transparent is overridden by compoundVariants
      'bg-transparent',
      // Focus outline reset (handled per-variant)
      'focus-visible:outline-none',
    ],
    // Label: 6px horizontal padding (matches MUI chip-overrides paddingInline: '6px')
    // Type is per-size (see the `size` variant) — large steps up to `chip/label-large`.
    label: [
      'inline-flex flex-col items-center justify-center px-[6px]',
      'transition-transform duration-150 ease-linear',
    ],
    deleteBtn: [
      // 24px touch target matching Figma — chip root clips the overflow
      'inline-flex shrink-0 items-center justify-center rounded-[100px]',
      'size-[24px] cursor-pointer border-none bg-transparent p-0',
      '[&>svg]:size-[24px]',
      // icon/fg/default = action.active = rgba(168,168,177,0.56). Bg/text colors snap both
      // ways (no transition) to match the chip body — only the deleteWrapper width/opacity
      // (defined below) is animated.
      'text-text-action-active',
      'hover:bg-states-hover hover:text-white',
      'focus-visible:outline-none focus-visible:ring-1',
    ],
    // Left inset is per-size (see the `size` variant): medium sits flush on the
    // chip's own 4px padding, small keeps its extra 6px.
    iconWrapper: ['inline-flex shrink-0 items-center'],
    deleteWrapper: [
      // In-flow flex child: collapses to 0 width, expands on hover.
      // The chip's own bg/border/ring naturally encompass this element.
      // -ml-[2px] cancels the parent's gap-[2px] so the wrapper takes true 0 space at rest.
      'inline-flex items-center overflow-hidden ml-[-2px]',
      'w-0 opacity-0',
      'transition-[width,opacity] duration-150',
      'group-hover/chip:w-6 group-hover/chip:opacity-100',
      // The delete button is tab-reachable — it must never hold focus while
      // 0-width/invisible (WCAG 2.4.7); reveal on its own focus too.
      'focus-within:w-6 focus-within:opacity-100',
    ],
    checkIcon: [
      // 14×14 check absolutely positioned inside the right indent zone (4px from
      // the chip's right edge — `right-0.5` is 4px on the 8px-base spacing scale).
      // The chip root is `relative` (see `root` slot).
      'absolute right-1 top-1/2 -translate-y-1/2',
      'inline-flex shrink-0 items-center justify-center size-[14px]',
      '[&>svg]:size-[14px]',
      'transition-opacity duration-150 ease-linear',
      'opacity-0',
    ],
  },
  variants: {
    variant: {
      filled: {},
      outlined: {
        root: 'border border-solid',
      },
      // Static-label only — no clickable/disabled/deletable compoundVariants.
      glass: {
        root: 'border border-solid',
      },
    },
    color: {
      default: {},
      primary: {},
    },
    size: {
      /**
       * Large: 48px height — MOBILE ONLY (Figma 22293:109014, Size=Large Line=Single-Select).
       * min-width equals the height so a one-glyph chip stays a circle, same rule as medium.
       */
      large: {
        root: 'h-12 p-1 min-w-12',
        label: typography['chip/label-large'],
        iconWrapper: '[&>svg]:size-5',
      },
      /**
       * Medium: 32px height
       * h-8=32px, p-1=4px (the spacing scale is 4px-based: `n` → 4n px)
       */
      medium: {
        root: 'h-8 p-1 min-w-8',
        label: typography['chip/label'],
        // No extra inset: in the DS the 6px belongs to the label slot, not the icon.
        iconWrapper: '[&>svg]:size-4',
      },
      /**
       * Small: 24px height
       * h-6=24px, px-1=4px, py-[3px]=3px
       */
      small: {
        root: 'h-6 px-1 py-[3px]',
        label: typography['chip/label'],
        iconWrapper: '[&>svg]:size-4 pl-[6px]',
      },
    },
    disabled: {
      true: {
        root: 'pointer-events-none',
        deleteWrapper: 'opacity-100', // always visible when disabled
      },
    },
    clickable: {
      true: {
        root: 'cursor-pointer',
      },
    },
    deletable: {
      true: {
        // z-index for overlay on neighbors; negative margin compensates for
        // the delete wrapper expanding from zero width to 24px so flow stays stable.
        root: 'hover:z-[10]  hover:mr-[-24px]',
      },
    },
    multiSelect: {
      true: {
        // Figma multi-select medium: 14px horizontal padding, 42px min-width (15619:235324).
        // Re-enables `transition-colors` locally so the default↔primary bg/text swap animates
        // (the base root list intentionally excludes color transitions — see comment there).
        root: 'gap-0 px-[14px] min-w-[42px] transition-colors duration-150 ease-linear',
        label: 'px-0',
      },
      false: {},
    },
    exclusive: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    // ─── DEFAULT FILLED ───────────────────────────────────────────────────────
    {
      variant: 'filled',
      color: 'default',
      className: {
        root: 'bg-secondary-main text-text-secondary',
      },
    },
    {
      variant: 'filled',
      color: 'default',
      clickable: true,
      className: {
        root: [
          // Hover: bg more opaque + text upgrades to text/primary
          'hover:bg-secondary-states-hover hover:text-text-primary',
          // Pressed
          'active:bg-secondary-states-active active:shadow-1',
          // Focused: fill darkens + inset ring (no layout shift)
          'focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary-main',
          'focus-visible:bg-secondary-states-active focus-visible:text-text-primary',
        ],
      },
    },
    // ─── DEFAULT OUTLINED ─────────────────────────────────────────────────────
    {
      variant: 'outlined',
      color: 'default',
      className: {
        root: 'bg-transparent border-secondary-states-border text-text-secondary',
      },
    },
    {
      variant: 'outlined',
      color: 'default',
      clickable: true,
      className: {
        root: [
          // Hover: border brightens to text/secondary + text upgrades to text/primary, no bg
          'hover:border-text-secondary hover:text-text-primary',
          'active:bg-states-focus active:border-secondary-states-activeBorder active:shadow-1',
          // Focused: action/focus bg + text/secondary border + text/primary label
          'focus-visible:border-text-secondary focus-visible:bg-states-focus focus-visible:text-text-primary',
        ],
      },
    },
    // ─── PRIMARY FILLED ───────────────────────────────────────────────────────
    {
      variant: 'filled',
      color: 'primary',
      className: {
        root: 'bg-primary-main text-primary-contrastText',
      },
    },
    {
      variant: 'filled',
      color: 'primary',
      clickable: true,
      className: {
        root: [
          // Hover: glow effect
          'hover:shadow-glow-primary',
          // Pressed: slightly darker + glow
          'active:bg-primary-dark active:shadow-glow-primary',
          // Focused: glow
          'focus-visible:bg-primary-dark',
        ],
      },
    },
    // ─── PRIMARY OUTLINED ─────────────────────────────────────────────────────
    {
      variant: 'outlined',
      color: 'primary',
      className: {
        root: 'bg-transparent border-primary-main text-primary-main',
      },
    },
    {
      variant: 'outlined',
      color: 'primary',
      clickable: true,
      className: {
        root: [
          'hover:bg-primary-states-hover hover:shadow-glow-primary',
          'active:bg-button-primary-hover active:border-primary-dark active:text-primary-dark active:shadow-1',
          // Focused: neutral base subtle bg
          'focus-visible:bg-primary-states-focus',
        ],
      },
    },
    // ─── GLASS DEFAULT ────────────────────────────────────────────────────────
    {
      variant: 'glass',
      color: 'default',
      className: {
        root: 'bg-white/[0.07] border-white/[0.18] text-text-secondary',
      },
    },
    // ─── GLASS PRIMARY ────────────────────────────────────────────────────────
    {
      variant: 'glass',
      color: 'primary',
      className: {
        root: 'bg-green-dim border-green-border text-primary-main',
      },
    },
    // ─── DISABLED ─────────────────────────────────────────────────────────────
    // Filled disabled (default + primary): replace bg with secondary/disabled, text with text/disabled
    {
      disabled: true,
      variant: 'filled',
      className: {
        root: 'bg-secondary-disabled text-text-disabled',
      },
    },
    // Default outlined disabled: replace border + text with disabled colors
    {
      disabled: true,
      variant: 'outlined',
      color: 'default',
      className: {
        root: 'bg-transparent border-secondary-states-active text-text-disabled',
      },
    },
    // Primary outlined disabled: fade whole chip to 38% opacity, preserve primary colors
    {
      disabled: true,
      variant: 'outlined',
      color: 'primary',
      className: {
        root: 'opacity-[0.38]',
      },
    },
    // ─── DELETABLE ──────────────────────────────────────────────────────────
    // Filled deletable: opaque bg so neighbor chips don't show through on overlap.
    // Semi-transparent secondary-* tokens are replaced with opaque paper-elevation equivalents.
    {
      variant: 'filled',
      color: 'default',
      deletable: true,
      className: {
        root: 'hover:bg-paper-elevation6 hover:text-text-primary active:bg-paper-elevation7 focus-visible:bg-paper-elevation7',
      },
    },
    // Outlined deletable: opaque bg matching page background so neighbor chips are hidden.
    {
      variant: 'outlined',
      color: 'default',
      deletable: true,
      className: {
        root: 'hover:bg-background-default',
      },
    },
    // Disabled deletable: always show button + always compensate margin
    {
      disabled: true,
      deletable: true,
      className: {
        root: 'mr-[-24px]',
        deleteWrapper: 'w-6 opacity-100',
      },
    },
    // The delete reveal adds 24px and the root compensates with a flat -24px, which
    // only cancels if the chip actually grows by 24px. Large's short labels rest ON its
    // 48px floor, so they grow by less and the margin over-pulls, dragging the row left.
    // Raising the floor by the same 24px restores the cancellation.
    //
    // `min-width` must also be IN the transition list: the base list eases margin only,
    // so a stepped floor applies on frame one while the compensation is still near zero
    // — the row kicks out, then settles. Scoped to large; medium and small keep theirs.
    {
      size: 'large',
      deletable: true,
      className: {
        root: [
          'hover:min-w-18',
          'transition-[margin,z-index,min-width,background-color,border-color,color,opacity] duration-150',
          'hover:transition-[margin,min-width,background-color,border-color,color,opacity] hover:duration-150',
        ],
      },
    },
    // Disabled is permanently revealed with a permanent -24px, so its floor is too.
    {
      size: 'large',
      deletable: true,
      disabled: true,
      className: {
        root: 'min-w-18',
      },
    },
    // ─── MULTI-SELECT ───────────────────────────────────────────────────────
    // On selection the label slides left so the check can fade in beside it rather than
    // on top of it. Width stays constant at every size — the check is absolutely
    // positioned, so nothing in the flex row grows and a wrapping row never re-wraps
    // under the user's finger. Large rescales the numbers; see its compound below.
    {
      multiSelect: true,
      color: 'primary',
      className: {
        label: '-translate-x-[6px]',
        checkIcon: 'opacity-100',
      },
    },
    // Large runs medium's mechanism with a 24px check. Both obey
    // `slide = check + inset + clearance - padding`:
    //   medium  14 + 4 + 2 - 14 = 6px   (padding from Figma 15619:235324)
    //   large   24 + 2 + 0 - 18 = 8px   (padding CHOSEN, not a Figma number)
    //
    // Figma has no padding to copy for this line at large: it sizes the chip with an
    // in-flow check (pl-10/pr-6 + 24px) that we deliberately do not use. 18px is picked
    // so the constant width sits between Figma's two states while keeping the slide
    // small; the slide then falls out of the formula. Worth a designer's confirmation.
    // Large can spend medium's 2px clearance because the check's glyph is inset 24.35%
    // inside its own box (Figma vector 15607:180718) — a 24px box carries 5.8px of air
    // per side against a 14px box's 3.4px, so touching boxes still read as a gap.
    //
    // Width is CONSTANT across selection. Figma draws 64px unselected (22293:109028)
    // and 76px selected (22293:109121); that growth was built and reverted, because a
    // chip that grows on tap shifts its neighbours and re-wraps a wrapping row. Figma's
    // 64px survives as the `min-w` floor, so short labels still match the design.
    {
      size: 'large',
      multiSelect: true,
      className: {
        root: 'px-[18px] min-w-16',
        checkIcon: 'right-0.5 size-6 [&>svg]:size-6',
      },
    },
    // The 8px half of the rule above. Keyed on `color` for the same reason medium is:
    // `primary` IS the selected state, so the slide arrives with the check's fade.
    {
      size: 'large',
      multiSelect: true,
      color: 'primary',
      className: {
        label: '-translate-x-2',
      },
    },
    // Exclusive `Any` chip never slides and never reveals the check, even in
    // `primary` color — overrides the rule above for this single chip.
    {
      multiSelect: true,
      exclusive: true,
      className: {
        label: 'translate-x-0',
        checkIcon: 'opacity-0',
      },
    },
  ],
  defaultVariants: {
    variant: 'filled',
    color: 'default',
    size: 'medium',
    disabled: false,
    clickable: false,
    deletable: false,
    multiSelect: false,
    exclusive: false,
  },
})

export type ChipVariants = Parameters<typeof chipVariants>[0]

export const subChipVariants = tv({
  base: [
    typography['chip/sublabel'],
    'inline-flex items-center rounded-[100px]',
    'h-[18px] px-[6px]',
    'transition-colors duration-150 select-none whitespace-nowrap',
    'border border-solid',
    // 4px trailing gap on the last sub-chip — Figma's "sub chips group" carries
    // pr-[4px], which with the chip's own p-1 makes the 8px right inset.
    'last:mr-1',
    // The delete wrapper is the last child of a deletable chip, so plain `last:`
    // misses the trailing 4px there. Scoped to direct children so a standalone
    // SubChip keeps plain `last:` semantics and gains no margin it never had.
    '[[data-ui=chip]>&:not(:has(~[data-ui=sub-chip]))]:mr-1',
  ],
  variants: {
    variant: {
      // Filled default (Figma 3517:50189): secondary.main bg, primary text
      filled: 'border-transparent bg-secondary-main text-text-primary',
      // Outlined default (Figma 3779:209878): transparent bg, enabled border, primary text
      outlined: 'bg-transparent border-secondary-states-border text-text-primary',
    },
    selected: {
      true: {},
      false: {},
    },
    clickable: {
      true: 'cursor-pointer focus-visible:outline-none focus-visible:ring-1',
    },
  },
  compoundVariants: [
    // Filled selected (Figma 3779:216723): primary border + subtle fill, primary text
    {
      variant: 'filled',
      selected: true,
      className: 'border-primary-main bg-button-primary-selected text-text-primary',
    },
    // Filled hover (Figma 3779:216715): more opaque bg + primary text
    {
      variant: 'filled',
      clickable: true,
      className: 'hover:bg-secondary-states-hover hover:text-text-primary',
    },
    // Outlined hover (Figma 3779:216717): warning state — outline border + hover bg
    {
      variant: 'outlined',
      clickable: true,
      className:
        'hover:border-button-warning-outline hover:bg-button-warning-hover hover:text-warning-main',
    },
    // Outlined selected: warning main border + warning selected bg
    {
      variant: 'outlined',
      selected: true,
      className: 'border-warning-main bg-button-warning-selected text-warning-main',
    },
  ],
  defaultVariants: {
    variant: 'filled',
    selected: false,
    clickable: false,
  },
})

export type SubChipVariants = Parameters<typeof subChipVariants>[0]
